import { createSlice } from "@reduxjs/toolkit";
import { logOut } from "../auth/authSlice";
import {
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "./notificationsActions";

const initialState = {
  items: [], // current page rows
  meta: null, // whole meta object from the last GET
  loading: false,
  markAllLoading: false,
  error: null,
};

const emptyByType = () => ({ order_status: 0, order_message: 0 });

// The three feed-wide counters. Every path that changes read state goes
// through here so they never drift apart.
const ensureMeta = (state) => {
  if (!state.meta) {
    state.meta = {
      unread_count: 0,
      read_count: 0,
      unread_by_type: emptyByType(),
    };
  }
  if (!state.meta.unread_by_type) {
    state.meta.unread_by_type = emptyByType();
  }
  return state.meta;
};

const bumpUnread = (state, type, delta) => {
  const meta = ensureMeta(state);
  meta.unread_count = Math.max(0, (meta.unread_count || 0) + delta);
  meta.read_count = Math.max(0, (meta.read_count || 0) - delta);
  if (type) {
    meta.unread_by_type[type] = Math.max(
      0,
      (meta.unread_by_type[type] || 0) + delta
    );
  }
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    clearNotifications: () => initialState,
    // `notification` frame: a new line was created. Prepended when the
    // drawer holds page 1 (any other page is the server's to order), the
    // counters bump by one. If nothing was fetched yet the meta is seeded
    // from this one row instead of dropping the increment.
    socketNotification: (state, action) => {
      const row = action?.payload;
      if (!row?.id) {
        return;
      }
      if (state.items.some((n) => n?.id === row.id)) {
        return; // duplicate after a reconnect
      }
      const onFirstPage = !state.meta || (state.meta.page || 1) === 1;
      if (onFirstPage) {
        state.items.unshift(row);
        const perPage = state.meta?.per_page;
        if (perPage && state.items.length > perPage) {
          state.items.length = perPage;
        }
      }
      const meta = ensureMeta(state);
      meta.count = (meta.count || 0) + 1;
      if (!row.is_read) {
        bumpUnread(state, row.type, 1);
      } else {
        meta.read_count = (meta.read_count || 0) + 1;
      }
    },
    // `notification_read` frame: rows changed read state somewhere (this
    // tab, another tab, the phone, or because the chat was opened). Flip the
    // loaded rows and REPLACE the counters with the payload's absolute ones.
    socketNotificationRead: (state, action) => {
      const payload = action?.payload;
      if (!payload) {
        return;
      }
      const ids = new Set(Array.isArray(payload.ids) ? payload.ids : []);
      const isRead = payload.is_read !== false;
      state.items.forEach((n) => {
        if (ids.has(n?.id)) {
          n.is_read = isRead;
          delete n._prev_is_read;
        }
      });
      const meta = ensureMeta(state);
      if (payload.unread_count !== undefined) {
        meta.unread_count = payload.unread_count;
      }
      if (payload.read_count !== undefined) {
        meta.read_count = payload.read_count;
      }
      if (payload.unread_by_type) {
        meta.unread_by_type = { ...emptyByType(), ...payload.unread_by_type };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = Array.isArray(action.payload?.results)
          ? action.payload.results
          : [];
        state.meta = action.payload?.meta
          ? { ...action.payload.meta, page: action.payload?.page || 1 }
          : null;
      })
      .addCase(getNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // markNotificationRead: optimistic flip, rolled back on reject.
      .addCase(markNotificationRead.pending, (state, action) => {
        const { id, is_read } = action.meta.arg || {};
        const item = state.items.find((n) => n?.id === id);
        if (!item || Boolean(item.is_read) === Boolean(is_read)) {
          return;
        }
        item._prev_is_read = item.is_read;
        item.is_read = is_read;
        bumpUnread(state, item.type, is_read ? -1 : 1);
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        const id = action.payload?.id ?? action.meta.arg?.id;
        const item = state.items.find((n) => n?.id === id);
        if (item) {
          delete item._prev_is_read;
          if (action.payload?.is_read !== undefined) {
            item.is_read = action.payload.is_read;
          }
        }
      })
      .addCase(markNotificationRead.rejected, (state, action) => {
        const { id } = action.meta.arg || {};
        const index = state.items.findIndex((n) => n?.id === id);
        const item = index !== -1 ? state.items[index] : null;
        if (item && item._prev_is_read !== undefined) {
          if (item.is_read !== item._prev_is_read) {
            bumpUnread(state, item.type, item.is_read ? 1 : -1);
          }
          item.is_read = item._prev_is_read;
          delete item._prev_is_read;
        }
        // A stale line: the order (and its lines) is gone. Drop it; the
        // panel refetches the page.
        if (item && action.payload?.status === 404) {
          if (!item.is_read) {
            bumpUnread(state, item.type, -1);
          }
          state.items.splice(index, 1);
          if (state.meta?.count) {
            state.meta.count = Math.max(0, state.meta.count - 1);
          }
        }
        state.error = action.payload;
      })
      .addCase(markAllNotificationsRead.pending, (state) => {
        state.markAllLoading = true;
      })
      .addCase(markAllNotificationsRead.fulfilled, (state) => {
        state.markAllLoading = false;
        state.items.forEach((n) => {
          n.is_read = true;
          delete n._prev_is_read;
        });
        const meta = ensureMeta(state);
        meta.read_count = (meta.read_count || 0) + (meta.unread_count || 0);
        meta.unread_count = 0;
        meta.unread_by_type = emptyByType();
      })
      .addCase(markAllNotificationsRead.rejected, (state, action) => {
        state.markAllLoading = false;
        state.error = action.payload;
      })
      // Logout wipes everything, like every customer-scoped slice.
      .addCase(logOut, () => initialState);
  },
});

export const {
  clearNotifications,
  socketNotification,
  socketNotificationRead,
} = notificationsSlice.actions;
export default notificationsSlice.reducer;
